import JSONModel from "sap/ui/model/json/JSONModel";
import BaseController from "./BaseController";
import AuthService, {
	isRequires2FA,
	SSO_ERROR_STORAGE_KEY,
} from "../service/AuthService";
import MessageToast from "sap/m/MessageToast";
import Dialog from "sap/m/Dialog";
import Input from "sap/m/Input";
import Button from "sap/m/Button";
import Label from "sap/m/Label";
import VBox from "sap/m/VBox";
import MessageBox from "sap/m/MessageBox";
import Bar from "sap/m/Bar";
import Title from "sap/m/Title";

/**
 * @namespace com.ang.portal.controller
 */
export default class Login extends BaseController {
	private _o2FADialog: Dialog | null = null;
	private _oForgotPasswordDialog: Dialog | null = null;

	public onInit(): void {
		const oModel = new JSONModel({
			email: "",
			password: "",
			remember: false,
			forgotPasswordEmail: "",
			twoFactorCode: "",
			tempToken: "",
			busy: false,
			forgotPasswordBusy: false,
			twoFactorBusy: false,
			errorMessage: "",
			emailState: "None",
			emailStateText: "",
			pwdState: "None",
		});
		this.setModel(oModel, "login");

		this.getRouter()
			.getRoute("login")
			.attachPatternMatched(() => this.onRouteMatched(), this);
	}

	private onRouteMatched(): void {
		if (AuthService.isAuthenticated()) {
			this.navTo("home", {}, true);
			return;
		}
		// Reset state when returning to login
		this.resetState();
		this.showSsoErrorIfPresent();
	}

	private async showSsoErrorIfPresent(): Promise<void> {
		const queryParams = new URLSearchParams(window.location.search);
		const queryError = queryParams.get("ssoError");
		const storedError = sessionStorage.getItem(SSO_ERROR_STORAGE_KEY);
		const ssoError = queryError || storedError;

		if (!ssoError) {
			return;
		}

		if (queryError) {
			queryParams.delete("ssoError");
			const newSearch = queryParams.toString();
			const newUrl =
				window.location.pathname +
				(newSearch ? `?${newSearch}` : "") +
				window.location.hash;
			window.history.replaceState(null, "", newUrl);
		}
		if (storedError) {
			sessionStorage.removeItem(SSO_ERROR_STORAGE_KEY);
		}

		const bundle = await this.getResourceBundle();
		const oModel = this.getModel("login") as JSONModel;
		oModel.setProperty(
			"/errorMessage",
			bundle.getText(`ssoError_${ssoError}`, [], true) ||
				bundle.getText("ssoErrorGeneric"),
		);
	}

	public onMicrosoftLoginPress(): void {
		AuthService.loginWithMicrosoft();
	}

	private resetState(): void {
		const oModel = this.getModel("login") as JSONModel;
		oModel.setProperty("/tempToken", "");
		oModel.setProperty("/twoFactorCode", "");
		oModel.setProperty("/forgotPasswordEmail", "");
		oModel.setProperty("/errorMessage", "");
	}

	public async onForgotPasswordPress(): Promise<void> {
		const oModel = this.getModel("login") as JSONModel;
		oModel.setProperty(
			"/forgotPasswordEmail",
			(oModel.getProperty("/email") as string) || "",
		);
		await this.openForgotPasswordDialog();
	}

	private async openForgotPasswordDialog(): Promise<void> {
		const oModel = this.getModel("login") as JSONModel;
		oModel.setProperty("/errorMessage", "");

		if (!this._oForgotPasswordDialog) {
			const bundle = await this.getResourceBundle();

			const oEmailInput = new Input({
				id: this.createId("forgotPasswordEmailInput"),
				type: "Email",
				value: "{login>/forgotPasswordEmail}",
				placeholder: bundle.getText("forgotPasswordEmailPlaceholder"),
				enabled: "{= !${login>/forgotPasswordBusy}}",
				width: "100%",
				submit: () => void this.onForgotPasswordSubmit(),
			});

			const oContent = new VBox({
				items: [
					new Label({
						text: bundle.getText("forgotPasswordEmailLabel"),
						required: true,
					}),
					oEmailInput,
				],
			}).addStyleClass("sapUiSmallMargin");

			const oCustomHeader = new Bar({
				contentMiddle: [
					new Title({ text: bundle.getText("forgotPasswordDialogTitle") }),
				],
				contentRight: [
					new Button({
						icon: "sap-icon://decline",
						type: "Transparent",
						press: () => this.closeForgotPasswordDialog(),
					}),
				],
			});

			this._oForgotPasswordDialog = new Dialog({
				id: this.createId("forgotPasswordDialog"),
				customHeader: oCustomHeader,
				type: "Message",
				contentWidth: "24rem",
				content: [oContent],
				beginButton: new Button({
					text: bundle.getText("forgotPasswordSubmitButton"),
					type: "Emphasized",
					enabled: "{= !${login>/forgotPasswordBusy}}",
					press: () => void this.onForgotPasswordSubmit(),
				}),
				afterOpen: () => {
					setTimeout(() => oEmailInput.focus(), 100);
				},
				escapeHandler: () => this.closeForgotPasswordDialog(),
			});

			this._oForgotPasswordDialog.setModel(oModel, "login");
			this.getView()?.addDependent(this._oForgotPasswordDialog);
		}

		this._oForgotPasswordDialog.open();
	}

	private closeForgotPasswordDialog(): void {
		if (this._oForgotPasswordDialog) {
			this._oForgotPasswordDialog.close();
		}
		const oModel = this.getModel("login") as JSONModel;
		oModel.setProperty("/forgotPasswordBusy", false);
	}

	private async onForgotPasswordSubmit(): Promise<void> {
		const oModel = this.getModel("login") as JSONModel;
		const email = (oModel.getProperty("/forgotPasswordEmail") as string).trim();
		const bundle = await this.getResourceBundle();

		if (!email) {
			oModel.setProperty(
				"/errorMessage",
				bundle.getText("forgotPasswordErrorRequired"),
			);
			return;
		}

		oModel.setProperty("/forgotPasswordBusy", true);
		oModel.setProperty("/errorMessage", "");

		try {
			const result = await AuthService.forgotPassword(email);
			this.closeForgotPasswordDialog();
			if (result.debugResetUrl) {
				console.info("[ForgotPassword] Debug reset URL:", result.debugResetUrl);
			}

			MessageBox.information(bundle.getText("forgotPasswordSuccess"), {
				title: bundle.getText("forgotPasswordDialogTitle"),
			});
		} catch (error) {
			oModel.setProperty(
				"/errorMessage",
				error instanceof Error && error.message
					? error.message
					: bundle.getText("forgotPasswordErrorGeneric"),
			);
		} finally {
			oModel.setProperty("/forgotPasswordBusy", false);
		}
	}

	/**
	 * Read a field from its DOM node, falling back to the model.
	 *
	 * Browser autofill writes straight to the input element without firing the
	 * change event sap.m.Input listens for, so the model can hold an older value
	 * than the one the user is looking at - that is how a login form showing the
	 * right address posted a stale one. The DOM is the source of truth; the model
	 * is resynced so validation and valueState stay consistent with it.
	 */
	private readFieldValue(sId: string, sPath: string): string {
		const oModel = this.getModel("login") as JSONModel;
		const oInput = this.byId(sId) as Input | undefined;
		const oDomRef = oInput?.getDomRef("inner") as HTMLInputElement | null;
		const sValue = oDomRef
			? oDomRef.value
			: ((oModel.getProperty(sPath) as string) ?? "");

		oModel.setProperty(sPath, sValue);
		return sValue;
	}

	public async onLoginPress(): Promise<void> {
		const oModel = this.getModel("login") as JSONModel;
		// Trimmed: a pasted address with a stray space fails as "Invalid login",
		// which is indistinguishable from a wrong password. The password is left
		// untouched - whitespace there may be deliberate.
		const email = this.readFieldValue("emailInput", "/email").trim();
		oModel.setProperty("/email", email);
		const password = this.readFieldValue("passwordInput", "/password");
		const bundle = await this.getResourceBundle();

		// Validate with Fiori valueState styling
		let bOk = true;
		if (!email) {
			oModel.setProperty("/emailState", "Error");
			oModel.setProperty(
				"/emailStateText",
				bundle.getText("loginErrorRequired"),
			);
			bOk = false;
		} else {
			oModel.setProperty("/emailState", "None");
			oModel.setProperty("/emailStateText", "");
		}
		if (!password) {
			oModel.setProperty("/pwdState", "Error");
			bOk = false;
		} else {
			oModel.setProperty("/pwdState", "None");
		}
		if (!bOk) {
			return;
		}

		oModel.setProperty("/busy", true);
		oModel.setProperty("/errorMessage", "");

		try {
			const result = await AuthService.login(email, password);

			if (isRequires2FA(result)) {
				// 2FA verification required - open verification dialog
				oModel.setProperty("/tempToken", result.tempToken);
				oModel.setProperty("/busy", false);
				this.open2FADialog();
				return;
			}

			// Normal login success
			const bundle = await this.getResourceBundle();
			MessageToast.show(bundle.getText("loginWelcome", [result.user.name]));
			this.navTo("home", {}, true);
		} catch {
			oModel.setProperty(
				"/errorMessage",
				(await this.getResourceBundle()).getText("loginErrorInvalid"),
			);
		} finally {
			oModel.setProperty("/busy", false);
		}
	}

	private async open2FADialog(): Promise<void> {
		const oModel = this.getModel("login") as JSONModel;
		oModel.setProperty("/twoFactorCode", "");

		if (!this._o2FADialog) {
			const bundle = await this.getResourceBundle();

			const oCodeInput = new Input({
				id: this.createId("twoFactorInput"),
				type: "Text",
				maxLength: 10,
				value: "{login>/twoFactorCode}",
				placeholder: bundle.getText("login2FAPlaceholder"),
				enabled: "{= !${login>/twoFactorBusy}}",
				width: "100%",
				submit: () => void this.onVerify2FA(),
			});

			const oContent = new VBox({
				items: [
					new Label({ text: bundle.getText("login2FALabel"), required: true }),
					oCodeInput,
				],
			}).addStyleClass("sapUiSmallMargin");

			const oCustomHeader = new Bar({
				contentMiddle: [
					new Title({ text: bundle.getText("login2FADialogTitle") }),
				],
				contentRight: [
					new Button({
						icon: "sap-icon://decline",
						type: "Transparent",
						press: () => this.close2FADialog(),
					}),
				],
			});

			this._o2FADialog = new Dialog({
				id: this.createId("twoFactorDialog"),
				customHeader: oCustomHeader,
				type: "Message",
				contentWidth: "20rem",
				content: [oContent],
				beginButton: new Button({
					text: bundle.getText("login2FAButtonText"),
					type: "Emphasized",
					enabled: "{= !${login>/twoFactorBusy}}",
					press: () => void this.onVerify2FA(),
				}),
				afterOpen: () => {
					setTimeout(() => oCodeInput.focus(), 100);
				},
				escapeHandler: () => this.close2FADialog(),
			});

			this._o2FADialog.setModel(oModel, "login");
			this.getView()?.addDependent(this._o2FADialog);
		}

		this._o2FADialog.open();
	}

	private close2FADialog(): void {
		if (this._o2FADialog) {
			this._o2FADialog.close();
		}
		const oModel = this.getModel("login") as JSONModel;
		oModel.setProperty("/twoFactorCode", "");
		oModel.setProperty("/tempToken", "");
	}

	private async onVerify2FA(): Promise<void> {
		const oModel = this.getModel("login") as JSONModel;
		const tempToken = oModel.getProperty("/tempToken") as string;
		const code = oModel.getProperty("/twoFactorCode") as string;
		const bundle = await this.getResourceBundle();

		if (!code || code.length < 6) {
			this.close2FADialog();
			oModel.setProperty(
				"/errorMessage",
				bundle.getText("login2FAErrorRequired"),
			);
			return;
		}

		oModel.setProperty("/twoFactorBusy", true);

		try {
			const result = await AuthService.verify2FA(tempToken, code);
			this.close2FADialog();
			MessageToast.show(bundle.getText("loginWelcome", [result.user.name]));
			this.navTo("home", {}, true);
		} catch {
			this.close2FADialog();
			oModel.setProperty(
				"/errorMessage",
				bundle.getText("login2FAErrorInvalid"),
			);
		} finally {
			oModel.setProperty("/twoFactorBusy", false);
		}
	}

	public onContactAdmin(): void {
		// Open mailto link or support route
		window.location.href =
			"mailto:support@ang.ch?subject=Portal Access Request";
	}
}
